import { Box, Button, ButtonGroup, Flex, FormLabel } from "@chakra-ui/react";
import { DebouncedInput } from "./DebouncedInput";

export type TextFilterMode = "contains" | "startsWith" | "equals" | "empty" | "notEmpty";

export interface TextFilterValue {
    mode: TextFilterMode;
    value?: string;
}

/** Returns true if the filter will actually constrain the query. */
export function isTextFilterActive(v: TextFilterValue | undefined): boolean {
    if (!v) return false;
    if (v.mode === "empty" || v.mode === "notEmpty") return true;
    return !!v.value;
}

const MODES: { value: TextFilterMode; label: string }[] = [
    { value: "contains", label: "Contains" },
    { value: "startsWith", label: "Starts With" },
    { value: "equals", label: "Equals" },
    { value: "empty", label: "Empty" },
    { value: "notEmpty", label: "Not Empty" },
];

interface TextFilterProps {
    label: string;
    value: TextFilterValue | undefined;
    onChange: (value: TextFilterValue | undefined) => void;
    isActive?: boolean;
}

export function TextFilter({ label, value, onChange, isActive }: TextFilterProps) {
    const mode: TextFilterMode = value?.mode ?? "contains";
    const showInput = mode === "contains" || mode === "startsWith" || mode === "equals";

    function setMode(m: TextFilterMode) {
        if (m === "empty" || m === "notEmpty") {
            // These modes don't use a text value
            onChange({ mode: m });
        } else {
            // contains / startsWith / equals — preserve any existing text, or clear if there's none
            onChange(value?.value ? { mode: m, value: value.value } : { mode: m });
        }
    }

    function setText(text: string) {
        if (!text) {
            onChange(mode === "contains" ? undefined : { mode });
        } else {
            onChange({ mode, value: text });
        }
    }

    return (
        <Flex direction="column" gap={1}>
            <FormLabel fontSize="xs" mb={0} display="flex" alignItems="center" gap={1}>
                {label}
                {isActive && <Box boxSize="6px" borderRadius="full" bg="blue.500" aria-label="Filter active" />}
            </FormLabel>
            <ButtonGroup size="xs" isAttached variant="outline">
                {MODES.map(m => {
                    const isActive = mode === m.value;
                    return (
                        <Button
                            key={m.value}
                            colorScheme={isActive ? "blue" : "gray"}
                            variant={isActive ? "solid" : "outline"}
                            onClick={() => setMode(m.value)}
                        >
                            {m.label}
                        </Button>
                    );
                })}
            </ButtonGroup>
            {showInput && <DebouncedInput size="sm" placeholder="Filter…" value={value?.value ?? ""} onChange={v => setText(v as string)} />}
        </Flex>
    );
}
